import * as Schema from "effect/Schema";

import { TrimmedNonEmptyString } from "./baseSchemas";

/**
 * The caller's Forge Cloud runtimes, as a client can usefully show them.
 *
 * The app only works while a machine you own is running and reachable. On a
 * laptop that is a mild constraint; on a phone it is the constraint, because
 * the whole point of the phone is that the laptop is shut. Listing the cloud
 * runtimes is the first half of removing it.
 *
 * CMMD owns this data and Forge's server already reads it, at
 * `/api/forge/context/runtimes`, with the caller's own CMMD JWT. Routing the
 * phone through the server rather than letting it call CMMD directly is not
 * incidental: the JWT is what CMMD uses to scope the answer to the caller's
 * organization, and the phone deliberately does not keep one. It keeps the
 * Forge-minted session token instead, which is the credential Forge's own
 * server accepts.
 *
 * This is a narrowed view rather than a passthrough. CMMD's row carries around
 * thirty fields, most of them Fly machine and volume plumbing that no client
 * should render or depend on. Widening it later is easy; unpicking a client
 * that grew a dependency on `flyVolumeName` is not.
 */

/**
 * What a person needs to decide whether to connect, and nothing else.
 *
 * `status` and `desiredState` are both kept because they disagree in the case
 * that matters: a runtime that is `suspended` but desired `running` is starting
 * up, and a picker that showed only one of the two would render that as either
 * "ready" or "asleep", both wrong.
 */
export const CloudRuntimeSummary = Schema.Struct({
  runtimeId: TrimmedNonEmptyString,
  organizationId: Schema.Int,
  displayName: Schema.NullOr(TrimmedNonEmptyString),
  environmentId: Schema.NullOr(TrimmedNonEmptyString),
  status: TrimmedNonEmptyString,
  desiredState: TrimmedNonEmptyString,
  readinessPhase: Schema.NullOr(TrimmedNonEmptyString),
  githubRepoFullName: Schema.NullOr(TrimmedNonEmptyString),
  lastActivityAt: Schema.NullOr(TrimmedNonEmptyString),
});
export type CloudRuntimeSummary = typeof CloudRuntimeSummary.Type;

export const CloudRuntimeListInput = Schema.Struct({});
export type CloudRuntimeListInput = typeof CloudRuntimeListInput.Type;

export const CloudRuntimeListResult = Schema.Struct({
  runtimes: Schema.Array(CloudRuntimeSummary),
});
export type CloudRuntimeListResult = typeof CloudRuntimeListResult.Type;

/**
 * Why the list could not be produced.
 *
 * `not_signed_in` is kept apart from `cmmd_unavailable` because they call for
 * opposite responses from a person: one is "sign in", the other is "wait". A
 * single failure would send someone to re-authenticate a session that is
 * working.
 */
export const CloudRuntimeListFailure = Schema.Literals([
  "not_signed_in",
  "cmmd_unavailable",
  "unexpected_response",
]);
export type CloudRuntimeListFailure = typeof CloudRuntimeListFailure.Type;

function decodedCloudRuntimeErrorMessage(props: object): string | undefined {
  if (!("message" in props)) return undefined;
  return typeof props.message === "string" ? props.message : undefined;
}

export class CloudRuntimeListError extends Schema.TaggedErrorClass<CloudRuntimeListError>()(
  "CloudRuntimeListError",
  {
    failure: Schema.optional(CloudRuntimeListFailure),
    detail: Schema.optional(TrimmedNonEmptyString),
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect),
  },
) {
  // @effect-diagnostics-next-line overriddenSchemaConstructor:off
  constructor(props: {
    readonly failure: CloudRuntimeListFailure;
    readonly detail?: string;
    readonly cause?: unknown;
  }) {
    super({
      ...props,
      message:
        decodedCloudRuntimeErrorMessage(props) ??
        DEFAULT_MESSAGES[props.failure] ??
        "Could not list your cloud environments.",
    } as any);
  }
}

/** One sentence per failure, in words that say what to do next. */
const DEFAULT_MESSAGES: Record<CloudRuntimeListFailure, string> = {
  not_signed_in: "Sign in to your Forge account to see your cloud environments.",
  cmmd_unavailable: "Could not reach Forge Cloud. Try again in a moment.",
  unexpected_response: "Forge Cloud returned something this build did not understand.",
};
